import React, { useState } from "react"
import {Copy, Check, Code2} from 'lucide-react'

interface CodeGeneratorProps {
  problemType: string
}

const languages = ["Python", "Java", "C++", "JavaScript"]

// Complete optimized code templates for all 5 problems
const codeTemplates: Record<string, Record<string, string>> = {
  fibonacci: {
    Python: `def fibonacci(n):
    """
    Time Complexity: O(n)
    Space Complexity: O(n)
    """
    # Base cases
    if n <= 1:
        return n
    
    # Create DP table
    dp = [0] * (n + 1)
    dp[1] = 1
    
    # Fill table using recurrence relation
    for i in range(2, n + 1):
        dp[i] = dp[i-1] + dp[i-2]
    
    return dp[n]

# Example usage
print(fibonacci(10))  # Output: 55`,
    Java: `public class Solution {
    /**
     * Time Complexity: O(n)
     * Space Complexity: O(n)
     */
    public static int fibonacci(int n) {
        // Base cases
        if (n <= 1) return n;
        
        // Create DP table
        int[] dp = new int[n + 1];
        dp[1] = 1;
        
        // Fill table using recurrence relation
        for (int i = 2; i <= n; i++) {
            dp[i] = dp[i-1] + dp[i-2];
        }
        
        return dp[n];
    }
    
    public static void main(String[] args) {
        System.out.println(fibonacci(10)); // Output: 55
    }
}`,
    "C++": `#include <vector>
#include <iostream>
using namespace std;

/**
 * Time Complexity: O(n)
 * Space Complexity: O(n)
 */
int fibonacci(int n) {
    // Base cases
    if (n <= 1) return n;
    
    // Create DP table
    vector<int> dp(n + 1);
    dp[1] = 1;
    
    // Fill table using recurrence relation
    for (int i = 2; i <= n; i++) {
        dp[i] = dp[i-1] + dp[i-2];
    }
    
    return dp[n];
}

int main() {
    cout << fibonacci(10) << endl; // Output: 55
    return 0;
}`,
    JavaScript: `/**
 * Time Complexity: O(n)
 * Space Complexity: O(n)
 */
function fibonacci(n) {
    // Base cases
    if (n <= 1) return n;
    
    // Create DP table
    const dp = new Array(n + 1).fill(0);
    dp[1] = 1;
    
    // Fill table using recurrence relation
    for (let i = 2; i <= n; i++) {
        dp[i] = dp[i-1] + dp[i-2];
    }
    
    return dp[n];
}

// Example usage
console.log(fibonacci(10)); // Output: 55`
  },
  knapsack: {
    Python: `def knapsack(weights, values, capacity):
    """
    0/1 Knapsack Problem
    Time Complexity: O(n * W)
    Space Complexity: O(n * W)
    """
    n = len(weights)
    
    # Create DP table: dp[i][w] = max value using first i items with capacity w
    dp = [[0 for _ in range(capacity + 1)] for _ in range(n + 1)]
    
    # Fill table
    for i in range(1, n + 1):
        for w in range(capacity + 1):
            # Don't include item i-1
            dp[i][w] = dp[i-1][w]
            
            # Include item i-1 if it fits
            if weights[i-1] <= w:
                dp[i][w] = max(dp[i][w], 
                              dp[i-1][w-weights[i-1]] + values[i-1])
    
    return dp[n][capacity]

# Example usage
weights = [1, 3, 4, 5]
values = [1, 4, 5, 7]
capacity = 7
print(knapsack(weights, values, capacity))  # Output: 9`,
    Java: `public class Solution {
    /**
     * 0/1 Knapsack Problem
     * Time Complexity: O(n * W)
     * Space Complexity: O(n * W)
     */
    public static int knapsack(int[] weights, int[] values, int capacity) {
        int n = weights.length;
        
        // Create DP table: dp[i][w] = max value using first i items
        int[][] dp = new int[n + 1][capacity + 1];
        
        // Fill table
        for (int i = 1; i <= n; i++) {
            for (int w = 0; w <= capacity; w++) {
                // Don't include item i-1
                dp[i][w] = dp[i-1][w];
                
                // Include item i-1 if it fits
                if (weights[i-1] <= w) {
                    dp[i][w] = Math.max(dp[i][w], 
                                       dp[i-1][w-weights[i-1]] + values[i-1]);
                }
            }
        }
        
        return dp[n][capacity];
    }
    
    public static void main(String[] args) {
        int[] weights = {1, 3, 4, 5};
        int[] values = {1, 4, 5, 7};
        System.out.println(knapsack(weights, values, 7)); // Output: 9
    }
}`,
    "C++": `#include <vector>
#include <algorithm>
#include <iostream>
using namespace std;

/**
 * 0/1 Knapsack Problem
 * Time Complexity: O(n * W)
 * Space Complexity: O(n * W)
 */
int knapsack(vector<int>& weights, vector<int>& values, int capacity) {
    int n = weights.size();
    
    // Create DP table: dp[i][w] = max value using first i items
    vector<vector<int>> dp(n + 1, vector<int>(capacity + 1, 0));
    
    // Fill table
    for (int i = 1; i <= n; i++) {
        for (int w = 0; w <= capacity; w++) {
            // Don't include item i-1
            dp[i][w] = dp[i-1][w];
            
            // Include item i-1 if it fits
            if (weights[i-1] <= w) {
                dp[i][w] = max(dp[i][w], 
                              dp[i-1][w-weights[i-1]] + values[i-1]);
            }
        }
    }
    
    return dp[n][capacity];
}

int main() {
    vector<int> weights = {1, 3, 4, 5};
    vector<int> values = {1, 4, 5, 7};
    cout << knapsack(weights, values, 7) << endl; // Output: 9
    return 0;
}`,
    JavaScript: `/**
 * 0/1 Knapsack Problem
 * Time Complexity: O(n * W)
 * Space Complexity: O(n * W)
 */
function knapsack(weights, values, capacity) {
    const n = weights.length;
    
    // Create DP table: dp[i][w] = max value using first i items
    const dp = Array(n + 1).fill(null)
        .map(() => Array(capacity + 1).fill(0));
    
    // Fill table
    for (let i = 1; i <= n; i++) {
        for (let w = 0; w <= capacity; w++) {
            // Don't include item i-1
            dp[i][w] = dp[i-1][w];
            
            // Include item i-1 if it fits
            if (weights[i-1] <= w) {
                dp[i][w] = Math.max(dp[i][w], 
                                   dp[i-1][w-weights[i-1]] + values[i-1]);
            }
        }
    }
    
    return dp[n][capacity];
}

// Example usage
const weights = [1, 3, 4, 5];
const values = [1, 4, 5, 7];
console.log(knapsack(weights, values, 7)); // Output: 9`
  },
  lcs: {
    Python: `def longest_common_subsequence(text1, text2):
    """
    Longest Common Subsequence
    Time Complexity: O(m * n)
    Space Complexity: O(m * n)
    """
    m, n = len(text1), len(text2)
    
    # Create DP table: dp[i][j] = LCS length of text1[0:i] and text2[0:j]
    dp = [[0 for _ in range(n + 1)] for _ in range(m + 1)]
    
    # Fill table
    for i in range(1, m + 1):
        for j in range(1, n + 1):
            if text1[i-1] == text2[j-1]:
                # Characters match: add 1 to diagonal
                dp[i][j] = dp[i-1][j-1] + 1
            else:
                # Take max from left or top
                dp[i][j] = max(dp[i-1][j], dp[i][j-1])
    
    return dp[m][n]

# Example usage
text1 = "abcde"
text2 = "ace"
print(longest_common_subsequence(text1, text2))  # Output: 3`,
    Java: `public class Solution {
    /**
     * Longest Common Subsequence
     * Time Complexity: O(m * n)
     * Space Complexity: O(m * n)
     */
    public static int longestCommonSubsequence(String text1, String text2) {
        int m = text1.length();
        int n = text2.length();
        
        // Create DP table: dp[i][j] = LCS length
        int[][] dp = new int[m + 1][n + 1];
        
        // Fill table
        for (int i = 1; i <= m; i++) {
            for (int j = 1; j <= n; j++) {
                if (text1.charAt(i-1) == text2.charAt(j-1)) {
                    // Characters match: add 1 to diagonal
                    dp[i][j] = dp[i-1][j-1] + 1;
                } else {
                    // Take max from left or top
                    dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1]);
                }
            }
        }
        
        return dp[m][n];
    }
    
    public static void main(String[] args) {
        System.out.println(longestCommonSubsequence("abcde", "ace")); // Output: 3
    }
}`,
    "C++": `#include <string>
#include <vector>
#include <algorithm>
#include <iostream>
using namespace std;

/**
 * Longest Common Subsequence
 * Time Complexity: O(m * n)
 * Space Complexity: O(m * n)
 */
int longestCommonSubsequence(string text1, string text2) {
    int m = text1.length();
    int n = text2.length();
    
    // Create DP table: dp[i][j] = LCS length
    vector<vector<int>> dp(m + 1, vector<int>(n + 1, 0));
    
    // Fill table
    for (int i = 1; i <= m; i++) {
        for (int j = 1; j <= n; j++) {
            if (text1[i-1] == text2[j-1]) {
                // Characters match: add 1 to diagonal
                dp[i][j] = dp[i-1][j-1] + 1;
            } else {
                // Take max from left or top
                dp[i][j] = max(dp[i-1][j], dp[i][j-1]);
            }
        }
    }
    
    return dp[m][n];
}

int main() {
    cout << longestCommonSubsequence("abcde", "ace") << endl; // Output: 3
    return 0;
}`,
    JavaScript: `/**
 * Longest Common Subsequence
 * Time Complexity: O(m * n)
 * Space Complexity: O(m * n)
 */
function longestCommonSubsequence(text1, text2) {
    const m = text1.length;
    const n = text2.length;
    
    // Create DP table: dp[i][j] = LCS length
    const dp = Array(m + 1).fill(null)
        .map(() => Array(n + 1).fill(0));
    
    // Fill table
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (text1[i-1] === text2[j-1]) {
                // Characters match: add 1 to diagonal
                dp[i][j] = dp[i-1][j-1] + 1;
            } else {
                // Take max from left or top
                dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1]);
            }
        }
    }
    
    return dp[m][n];
}

// Example usage
console.log(longestCommonSubsequence("abcde", "ace")); // Output: 3`
  },
  climbing: {
    Python: `def climb_stairs(n):
    """
    Climbing Stairs Problem
    Time Complexity: O(n)
    Space Complexity: O(n)
    """
    # Base cases
    if n <= 2:
        return n
    
    # Create DP table: dp[i] = ways to reach step i
    dp = [0] * (n + 1)
    dp[1] = 1  # 1 way to reach step 1
    dp[2] = 2  # 2 ways to reach step 2
    
    # Fill table using recurrence relation
    # At each step, you can come from (i-1) or (i-2)
    for i in range(3, n + 1):
        dp[i] = dp[i-1] + dp[i-2]
    
    return dp[n]

# Example usage
print(climb_stairs(5))  # Output: 8`,
    Java: `public class Solution {
    /**
     * Climbing Stairs Problem
     * Time Complexity: O(n)
     * Space Complexity: O(n)
     */
    public static int climbStairs(int n) {
        // Base cases
        if (n <= 2) return n;
        
        // Create DP table: dp[i] = ways to reach step i
        int[] dp = new int[n + 1];
        dp[1] = 1;  // 1 way to reach step 1
        dp[2] = 2;  // 2 ways to reach step 2
        
        // Fill table using recurrence relation
        // At each step, you can come from (i-1) or (i-2)
        for (int i = 3; i <= n; i++) {
            dp[i] = dp[i-1] + dp[i-2];
        }
        
        return dp[n];
    }
    
    public static void main(String[] args) {
        System.out.println(climbStairs(5)); // Output: 8
    }
}`,
    "C++": `#include <vector>
#include <iostream>
using namespace std;

/**
 * Climbing Stairs Problem
 * Time Complexity: O(n)
 * Space Complexity: O(n)
 */
int climbStairs(int n) {
    // Base cases
    if (n <= 2) return n;
    
    // Create DP table: dp[i] = ways to reach step i
    vector<int> dp(n + 1);
    dp[1] = 1;  // 1 way to reach step 1
    dp[2] = 2;  // 2 ways to reach step 2
    
    // Fill table using recurrence relation
    // At each step, you can come from (i-1) or (i-2)
    for (int i = 3; i <= n; i++) {
        dp[i] = dp[i-1] + dp[i-2];
    }
    
    return dp[n];
}

int main() {
    cout << climbStairs(5) << endl; // Output: 8
    return 0;
}`,
    JavaScript: `/**
 * Climbing Stairs Problem
 * Time Complexity: O(n)
 * Space Complexity: O(n)
 */
function climbStairs(n) {
    // Base cases
    if (n <= 2) return n;
    
    // Create DP table: dp[i] = ways to reach step i
    const dp = new Array(n + 1);
    dp[1] = 1;  // 1 way to reach step 1
    dp[2] = 2;  // 2 ways to reach step 2
    
    // Fill table using recurrence relation
    // At each step, you can come from (i-1) or (i-2)
    for (let i = 3; i <= n; i++) {
        dp[i] = dp[i-1] + dp[i-2];
    }
    
    return dp[n];
}

// Example usage
console.log(climbStairs(5)); // Output: 8`
  },
  coin: {
    Python: `def coin_change(coins, amount):
    """
    Coin Change Problem (Minimum Coins)
    Time Complexity: O(amount * n)
    Space Complexity: O(amount)
    """
    # Create DP table: dp[i] = min coins needed for amount i
    dp = [float('inf')] * (amount + 1)
    dp[0] = 0  # Base case: 0 coins for amount 0
    
    # Fill table
    for i in range(1, amount + 1):
        # Try using each coin
        for coin in coins:
            if coin <= i:
                # Update min coins if using this coin is better
                dp[i] = min(dp[i], dp[i - coin] + 1)
    
    # Return result (-1 if impossible)
    return dp[amount] if dp[amount] != float('inf') else -1

# Example usage
coins = [1, 2, 5]
amount = 11
print(coin_change(coins, amount))  # Output: 3 (5+5+1)`,
    Java: `public class Solution {
    /**
     * Coin Change Problem (Minimum Coins)
     * Time Complexity: O(amount * n)
     * Space Complexity: O(amount)
     */
    public static int coinChange(int[] coins, int amount) {
        // Create DP table: dp[i] = min coins needed for amount i
        int[] dp = new int[amount + 1];
        Arrays.fill(dp, amount + 1);  // Use amount+1 as "infinity"
        dp[0] = 0;  // Base case: 0 coins for amount 0
        
        // Fill table
        for (int i = 1; i <= amount; i++) {
            // Try using each coin
            for (int coin : coins) {
                if (coin <= i) {
                    // Update min coins if using this coin is better
                    dp[i] = Math.min(dp[i], dp[i - coin] + 1);
                }
            }
        }
        
        // Return result (-1 if impossible)
        return dp[amount] > amount ? -1 : dp[amount];
    }
    
    public static void main(String[] args) {
        int[] coins = {1, 2, 5};
        System.out.println(coinChange(coins, 11)); // Output: 3
    }
}`,
    "C++": `#include <vector>
#include <algorithm>
#include <climits>
#include <iostream>
using namespace std;

/**
 * Coin Change Problem (Minimum Coins)
 * Time Complexity: O(amount * n)
 * Space Complexity: O(amount)
 */
int coinChange(vector<int>& coins, int amount) {
    // Create DP table: dp[i] = min coins needed for amount i
    vector<int> dp(amount + 1, INT_MAX);
    dp[0] = 0;  // Base case: 0 coins for amount 0
    
    // Fill table
    for (int i = 1; i <= amount; i++) {
        // Try using each coin
        for (int coin : coins) {
            if (coin <= i && dp[i - coin] != INT_MAX) {
                // Update min coins if using this coin is better
                dp[i] = min(dp[i], dp[i - coin] + 1);
            }
        }
    }
    
    // Return result (-1 if impossible)
    return dp[amount] == INT_MAX ? -1 : dp[amount];
}

int main() {
    vector<int> coins = {1, 2, 5};
    cout << coinChange(coins, 11) << endl; // Output: 3
    return 0;
}`,
    JavaScript: `/**
 * Coin Change Problem (Minimum Coins)
 * Time Complexity: O(amount * n)
 * Space Complexity: O(amount)
 */
function coinChange(coins, amount) {
    // Create DP table: dp[i] = min coins needed for amount i
    const dp = new Array(amount + 1).fill(Infinity);
    dp[0] = 0;  // Base case: 0 coins for amount 0
    
    // Fill table
    for (let i = 1; i <= amount; i++) {
        // Try using each coin
        for (const coin of coins) {
            if (coin <= i) {
                // Update min coins if using this coin is better
                dp[i] = Math.min(dp[i], dp[i - coin] + 1);
            }
        }
    }
    
    // Return result (-1 if impossible)
    return dp[amount] === Infinity ? -1 : dp[amount];
}

// Example usage
const coins = [1, 2, 5];
console.log(coinChange(coins, 11)); // Output: 3 (5+5+1)`
  }
}

// Problem metadata for display
const problemInfo: Record<string, { name: string; description: string }> = {
  fibonacci: {
    name: "Fibonacci Sequence",
    description: "Calculate the nth Fibonacci number using dynamic programming"
  },
  knapsack: {
    name: "0/1 Knapsack Problem",
    description: "Maximize value within weight capacity constraint"
  },
  lcs: {
    name: "Longest Common Subsequence",
    description: "Find the longest subsequence present in both strings"
  },
  climbing: {
    name: "Climbing Stairs",
    description: "Count distinct ways to climb n stairs (1 or 2 steps at a time)"
  },
  coin: {
    name: "Coin Change",
    description: "Find minimum coins needed to make a target amount"
  }
}

export default function CodeGenerator({ problemType }: CodeGeneratorProps) {
  const [selectedLanguage, setSelectedLanguage] = useState("Python")
  const [copied, setCopied] = useState(false)

  const code = codeTemplates[problemType]?.[selectedLanguage] || "// Code template not available"
  const info = problemInfo[problemType] || { name: "Unknown", description: "" }

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <section id="code-generator" className="mb-12 scroll-mt-20">
      <div className="flex items-center gap-3 mb-6">
        <Code2 className="w-8 h-8 text-emerald-400" />
        <h2 className="text-3xl font-bold">Generate Optimized Code</h2>
      </div>

      {/* Problem Info Card */}
      <div className="bg-gradient-to-r from-emerald-900/30 to-emerald-900/10 border border-emerald-500/30 rounded-xl p-4 mb-6">
        <h3 className="text-xl font-bold text-emerald-400 mb-1">{info.name}</h3>
        <p className="text-slate-300">{info.description}</p>
      </div>
      
      <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden shadow-2xl">
        {/* Language Selector & Copy Button */}
        <div className="flex items-center justify-between border-b border-slate-700 p-4 bg-slate-800/50">
          <div className="flex gap-2 flex-wrap">
            {languages.map((lang) => (
              <button
                key={lang}
                onClick={() => setSelectedLanguage(lang)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedLanguage === lang
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/50"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
          
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-all hover:shadow-lg"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy Code</span>
              </>
            )}
          </button>
        </div>

        {/* Code Display with Syntax Highlighting */}
        <div className="p-6 bg-slate-950 overflow-x-auto">
          <pre className="text-sm font-mono leading-relaxed">
            <code className="text-slate-300">{code}</code>
          </pre>
        </div>

        {/* Code Stats Footer */}
        <div className="border-t border-slate-700 p-4 bg-slate-800/30 flex items-center gap-4 text-sm text-slate-400">
          <span className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            Interview-Ready
          </span>
          <span className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
            Optimized
          </span>
          <span className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            Production-Grade
          </span>
        </div>
      </div>

      {/* Pro Tip */}
      <div className="mt-6 p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
        <p className="text-slate-300">
          <span className="font-bold text-cyan-400">💡 Pro Tip:</span> All code templates include time/space complexity analysis and are optimized for technical interviews.
        </p>
      </div>
    </section>
  )
}